import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { OpenInNewOutlined as OpenInNewIcon } from '@mui/icons-material';

interface DocumentPreviewDialogProps {
  open: boolean;
  title: string;
  previewUrl: string | null;
  contentType?: string;
  fileFormat?: string;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onDownload?: () => void;
}

const normalizeFileFormat = (fileFormat?: string) =>
  fileFormat?.replace(/^\./, '').toLowerCase();

const isPreviewableText = (contentType?: string, fileFormat?: string) => {
  const normalized = normalizeFileFormat(fileFormat);
  return (
    contentType?.startsWith('text/') ||
    contentType === 'application/json' ||
    ['txt', 'json', 'csv', 'md'].includes(normalized || '')
  );
};

const getPreviewKind = (contentType?: string, fileFormat?: string) => {
  const normalized = normalizeFileFormat(fileFormat);

  if (contentType === 'application/pdf' || normalized === 'pdf') return 'pdf';
  if (
    contentType?.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(normalized || '')
  ) {
    return 'image';
  }
  if (
    contentType?.startsWith('audio/') ||
    ['mp3', 'wav', 'm4a', 'ogg', 'webm'].includes(normalized || '')
  ) {
    return 'audio';
  }
  if (
    contentType?.startsWith('video/') ||
    ['mp4', 'mov', 'webm'].includes(normalized || '')
  ) {
    return 'video';
  }
  if (isPreviewableText(contentType, fileFormat)) return 'text';

  return 'unsupported';
};

export const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  title,
  previewUrl,
  contentType,
  fileFormat,
  loading = false,
  error,
  onClose,
  onDownload
}) => {
  const previewKind = getPreviewKind(contentType, fileFormat);
  const canOpenInNewTab = Boolean(previewUrl && previewKind !== 'unsupported');

  const handleOpenInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: '92vh', sm: '86vh' },
          borderRadius: '8px'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          pr: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading preview...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, width: '100%' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : previewUrl && previewKind === 'image' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              p: 2,
              minWidth: 0
            }}
          >
            <Box
              component="img"
              src={previewUrl}
              alt={title}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        ) : previewUrl && previewKind === 'audio' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3
            }}
          >
            <Box
              component="audio"
              src={previewUrl}
              controls
              sx={{ width: '100%' }}
            />
          </Box>
        ) : previewUrl && previewKind === 'video' ? (
          <Box
            component="video"
            src={previewUrl}
            controls
            sx={{ width: '100%', height: '100%', bgcolor: 'black' }}
          />
        ) : previewUrl && ['pdf', 'text'].includes(previewKind) ? (
          <Box
            component="iframe"
            src={previewUrl}
            title={title}
            sx={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3
            }}
          >
            <Alert severity="info" sx={{ maxWidth: 520 }}>
              Preview is not available for this file type.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose}>Close</Button>
        {canOpenInNewTab && (
          <Button onClick={handleOpenInNewTab} startIcon={<OpenInNewIcon />}>
            Open in new tab
          </Button>
        )}
        {onDownload && (
          <Button onClick={onDownload} variant="contained">
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
